import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import TableauNotes from '../components/TableauNotes';
import { Search } from 'lucide-react';

const SaisieNotes = () => {
  const [activeYear, setActiveYear] = useState(null);
  const [classesParSection, setClassesParSection] = useState({ college: [], lycee: [] });
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedTrimestre, setSelectedTrimestre] = useState('');
  const [eleves, setEleves] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTableau, setShowTableau] = useState(false);

  // States pour les dropdowns
const [classeSearchTerm, setClasseSearchTerm] = useState('');
const [isClasseDropdownOpen, setIsClasseDropdownOpen] = useState(false);
const [isTrimestreDropdownOpen, setIsTrimestreDropdownOpen] = useState(false);

  // Charger l'année active
  useEffect(() => {
    const chargerAnneeActive = async () => {
      try {
        const result = await api.anneesScolaires.getActive();
        if (result.success && result.data) setActiveYear(result.data);
      } catch (error) {
        console.error('Erreur chargement année:', error);
      }
    };
    chargerAnneeActive();
  }, []);

  // Charger les classes du collège/lycée
  useEffect(() => {
    const chargerClasses = async () => {
      try {
        setLoading(true);
        const result = await api.classes.getAll();
        const classes = result.success ? result.data : result;
        
        // Grouper les classes par section
        const college = classes.filter(c => 
          c.section_nom?.toLowerCase() === 'collège' || 
          c.section_nom?.toLowerCase() === 'college'
        );
        
        const lycee = classes.filter(c => 
          c.section_nom?.toLowerCase() === 'lycée' || 
          c.section_nom?.toLowerCase() === 'lycee'
        );
        
        setClassesParSection({ college, lycee });
        
        console.log('📊 Classes groupées:', { college, lycee });
      } catch (error) {
        console.error('Erreur chargement classes:', error);
      } finally {
        setLoading(false);
      }
    };
    chargerClasses();
  }, []);


  // Fermer les dropdowns au clic extérieur
useEffect(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest('.relative')) {
      setIsClasseDropdownOpen(false);
      setIsTrimestreDropdownOpen(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);


  // Handler changement de classe
  const handleClasseChange = async (classeId) => {
    setSelectedClasse(classeId);
    setShowTableau(false);
    
    if (classeId) {
      try {
        setLoading(true);
        const [elevesData, matieresData] = await Promise.all([
          api.classes.getEleves(classeId),
          api.scolarite.getMatieres(classeId)
        ]);
        setEleves(elevesData);
        setMatieres(matieresData);
        
        // Si un trimestre est déjà sélectionné, afficher le tableau automatiquement
        if (selectedTrimestre) {
          setShowTableau(true);
        }
      } catch (error) {
        console.error('Erreur chargement données:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setEleves([]);
      setMatieres([]);
    }
  };

  // Handler changement de trimestre
  const handleTrimestreChange = (trimestre) => {
    setSelectedTrimestre(trimestre);
    setShowTableau(trimestre && selectedClasse);
  };

  // Loader année active
  if (!activeYear) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#246990]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#246990] to-[#3084b5] rounded-xl shadow-lg p-6 text-white">
        <h1 className="text-xl font-bold mb-2">Saisie des Notes - Collège & Lycée</h1>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-6">
        

  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
  
  {/* ✅ CLASSE - Dropdown avec recherche et groupes Collège/Lycée */}
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      Classe <span className="text-red-500">*</span>
    </label>
    
    <div className="relative">
      {/* ⬅️ AJOUT : Icône Search */}
      <Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 pointer-events-none z-10" />
      
      <input
        type="text"
        placeholder="Rechercher une classe..."
        value={(() => {
          if (selectedClasse) {
            const touteClasses = [...classesParSection.college, ...classesParSection.lycee];
            const classe = touteClasses.find(c => c.id.toString() === selectedClasse);
            return classe ? classe.nom : '';
          }
          return classeSearchTerm;
        })()}
        onChange={(e) => {
          setClasseSearchTerm(e.target.value);
          setIsClasseDropdownOpen(true);
          if (selectedClasse) {
            setSelectedClasse('');
          }
        }}
        onFocus={() => setIsClasseDropdownOpen(true)}
        disabled={loading}
        className="w-full pl-10 pr-10 py-3 text-base font-medium border-2 border-gray-200 rounded-lg 
                  bg-white hover:border-[#246990] focus:border-[#246990] focus:ring-4 focus:ring-[#246990]/10 
                  outline-none transition-all
                  disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
      />
      
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {/* ✅ Dropdown vers le BAS avec groupes */}
      {isClasseDropdownOpen && !selectedClasse && (
        <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {(() => {
            const filteredCollege = classesParSection.college
              .filter(classe => classe.nom.toLowerCase().includes(classeSearchTerm.toLowerCase()))
              .sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
            
            const filteredLycee = classesParSection.lycee
              .filter(classe => classe.nom.toLowerCase().includes(classeSearchTerm.toLowerCase()))
              .sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
            
            const hasResults = filteredCollege.length > 0 || filteredLycee.length > 0;
            
            if (!hasResults) {
              return (
                <div className="p-4 text-center text-gray-500 text-sm">
                  Aucune classe trouvée
                </div>
              );
            }
            
            return (
              <>
                {/* Groupe Collège */}
                {filteredCollege.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-gray-100 border-b font-bold text-gray-700 text-sm">
                      Collège
                    </div>
                    {filteredCollege.map((classe) => (
                      <div
                        key={classe.id}
                        onClick={() => {
                          handleClasseChange(classe.id.toString());
                          setSelectedClasse(classe.id.toString());
                          setClasseSearchTerm('');
                          setIsClasseDropdownOpen(false);
                        }}
                        className="px-4 py-3 pl-8 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900 text-base">
                          {classe.nom}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Groupe Lycée */}
                {filteredLycee.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-gray-100 border-b font-bold text-gray-700 text-sm">
                      Lycée
                    </div>
                    {filteredLycee.map((classe) => (
                      <div
                        key={classe.id}
                        onClick={() => {
                          handleClasseChange(classe.id.toString());
                          setSelectedClasse(classe.id.toString());
                          setClasseSearchTerm('');
                          setIsClasseDropdownOpen(false);
                        }}
                        className="px-4 py-3 pl-8 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900 text-base">
                          {classe.nom}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  </div>

  {/* ✅ TRIMESTRE - Dropdown simple */}
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      Trimestre <span className="text-red-500">*</span>
    </label>
    
    <div className="relative">
      <input
        type="text"
        placeholder="Sélectionner un trimestre..."
        value={(() => {
          if (selectedTrimestre === '1') return '1er Trimestre';
          if (selectedTrimestre === '2') return '2ème Trimestre';
          if (selectedTrimestre === '3') return '3ème Trimestre';
          return '';
        })()}
        onFocus={() => setIsTrimestreDropdownOpen(true)}
        readOnly
        disabled={loading || !selectedClasse}
        className="w-full px-4 py-3 pr-10 text-base font-medium border-2 border-gray-200 rounded-lg 
                  bg-white hover:border-[#319190] focus:border-[#319190] focus:ring-4 focus:ring-[#319190]/10 
                  outline-none transition-all cursor-pointer
                  disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
      />
      
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {/* ✅ Dropdown vers le BAS */}
      {isTrimestreDropdownOpen && (
        <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {[
            { value: '1', label: '1er Trimestre' },
            { value: '2', label: '2ème Trimestre' },
            { value: '3', label: '3ème Trimestre' }
          ].map((trimestre) => (
            <div
              key={trimestre.value}
              onClick={() => {
                handleTrimestreChange(trimestre.value);
                setSelectedTrimestre(trimestre.value);
                setIsTrimestreDropdownOpen(false);
              }}
              className={`px-4 py-3 hover:bg-teal-50 cursor-pointer border-b last:border-b-0 transition-colors
                ${selectedTrimestre === trimestre.value ? 'bg-teal-100' : ''}`}
            >
              <div className="font-medium text-gray-900 text-base">
                {trimestre.label}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
</div>
 

        {/* Stats */}
        {selectedClasse && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex gap-8 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#246990]"></div>
                <span className="text-gray-800">Élèves :</span>
                <span className="font-bold text-[#246990]">{eleves.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#319190]"></div>
                <span className="text-gray-800">Matières :</span>
                <span className="font-bold text-[#319190]">{matieres.length}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loader */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#246990]"></div>
        </div>
      )}

      {/* Tableau */}
   
      {!loading && selectedClasse && selectedTrimestre && (
        <TableauNotes
          eleves={eleves}
          matieres={matieres}
          trimestre={selectedTrimestre}
          annee_scolaire_id={activeYear.id}
          classeId={parseInt(selectedClasse)}
          onSave={() => {
            console.log('✅ Notes sauvegardées');
          }}
        />
      )}

      {/* Messages */}
      {!showTableau && !loading && selectedClasse && !selectedTrimestre && (
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-6">
          <p className="text-amber-800 font-medium">
            Veuillez sélectionner un trimestre pour continuer
          </p>
        </div>
      )}

      {!showTableau && !loading && !selectedClasse && (
        <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-6">
          <p className="text-blue-800 font-medium">
            Sélectionnez une classe et un trimestre pour commencer la saisie
          </p>
        </div>
      )}



      {/* Alerte Information Urgente */}
      <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6 shadow-md">
        <div className="flex items-start gap-3">
          <svg 
            className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
            />
          </svg>
          <div className="flex-1">
            <h3 className="text-red-800 font-bold text-lg mb-3">
              INFORMATION IMPORTANTE
            </h3>
            
            <div className="space-y-4 text-red-800">
              <div>
                <h4 className="font-semibold mb-2">1. Vérification des erreurs de calcul</h4>
                <p className="text-sm leading-relaxed">
                  Nous avons constaté que certains enseignants remettent des fiches contenant des erreurs de calcul. 
                  <span className="font-semibold"> Vérifiez minutieusement toutes les notes avant la saisie.</span> En cas d'erreur, 
                  contactez l'enseignant concerné, le directeur ou le responsable pédagogique pour valider la note correcte.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">2. Matières à coefficient supérieur à 1</h4>
                <p className="text-sm leading-relaxed mb-2">
                  Les enseignants remettent parfois des notes déjà multipliées par leur coefficient. 
                  <span className="font-semibold"> Vous devez saisir la note brute (non calculée) :</span>
                </p>
                <ul className="text-sm space-y-1 ml-4 list-disc">
                  <li>Divisez la note par son coefficient (ex: 15 ÷ 2 = 7,5)</li>
                  <li>Saisissez la note brute obtenue (7,5)</li>
                  <li>L'application appliquera automatiquement le coefficient</li>
                </ul>
                <p className="text-sm font-semibold mt-2 bg-red-100 p-2 rounded">
                  Attention : Si vous saisissez la note déjà calculée, l'application la multipliera à nouveau, 
                  ce qui donnera une note fausse !
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>

    
  );
};

export default SaisieNotes;